import { type FC, memo, type ReactNode, useMemo } from 'react'

import { cn } from '@/lib/utils'

import { partitionAttachments } from './chat-input-attachment.helper'
import type { ChatAttachment } from './chat-input-attachment.interface'
import { MAX_IMAGE_ATTACHMENTS } from './chat-input-attachment.interface'
import { ChatInputFileList } from './chat-input-file-list.atom'
import { ChatInputImageList } from './chat-input-image-list.atom'
import { ChatInputUploadProgress } from './chat-input-upload-progress.atom'

export interface ChatInputAttachmentAreaProps {
  attachmentList: ChatAttachment[]
  onRemoveAttachment: (id: string) => void
  maxImageCount?: number
  isUploading?: boolean
  uploadProgress?: number
  className?: string
}

const ChatInputAttachmentAreaBase: FC<ChatInputAttachmentAreaProps> = ({
  attachmentList,
  onRemoveAttachment,
  maxImageCount = MAX_IMAGE_ATTACHMENTS,
  isUploading = false,
  uploadProgress = 0,
  className,
}): ReactNode => {
  const { fileList, imageList } = useMemo(
    () => partitionAttachments(attachmentList, maxImageCount),
    [attachmentList, maxImageCount]
  )

  const hasFiles = fileList.length > 0
  const hasImages = imageList.length > 0

  if (!hasFiles && !hasImages && !isUploading) {
    return null
  }

  return (
    <div className={cn('chat-input-attachment-area space-y-2', className)}>
      {isUploading && <ChatInputUploadProgress progress={uploadProgress} />}
      {hasFiles && <ChatInputFileList fileList={fileList} onRemove={onRemoveAttachment} />}
      {hasImages && <ChatInputImageList imageList={imageList} onRemove={onRemoveAttachment} />}
    </div>
  )
}

export const ChatInputAttachmentArea = memo(ChatInputAttachmentAreaBase)

ChatInputAttachmentArea.displayName = 'ChatInputAttachmentArea'
